// Extracted from original/src/App.tsx:40760-41484 (`activeTab === "store"`, ADR-08 §2.1 "copy, then cut logic"):
//   header bar: icon tile, title, "n مادة مسجلة" badge, the four header buttons            :40768-40861
//   sub-tab segmented bar (inventory / sales invoices / returns / transfer archive) + badges :40864-40956
//   inventory: warehouse filter pills, the four KPI cards, product cards, the two dashed tiles :40960-41256
//   transfer archive: header + search + "إجراء تحويل جديد", three stat tiles, table          :41292-41481
// Kept: every class string, icon and size, the motion frames, the pill counters, the dashed "add" / "new sell list"
// tiles, the card hover actions, the stock badge colours (green > 0, rose = 0), the warehouse badge on each card.
// Cut: `filteredProducts` / KPI arithmetic over stored stock × cost (App.tsx:41011-41016, R-25) → the server's
// inventory accounts (D-110, D-292) passed in as preformatted labels; the text matching of the badges and of the
// transfer archive (`item.includes("تحويل مخزني")`, R-28, R-31) → lists classified by operation type (D-115), so every
// badge equals its list; the role check `role === 'admin' || 'super_manager'` on the warehouse button (R-30) →
// `canManageWarehouses` (`manage_warehouses`, D-114, on every path incl. the archive's "إجراء تحويل جديد").
// Changed by decision: one card per product × warehouse holding stock (stock lives per warehouse, D-110; the old app
// had one product row per warehouse, I-3), a product with no stock anywhere shows one card at zero; the card's trash
// button opens the write-off / archive dialog (D-114: no delete with stock, a write-off is a loss with PIN + reason);
// a product card gets an edit button (name, sale price, D-114); cost = the account's exact average (D-292).
// Added: data-testid attributes.
import { motion } from 'motion/react';
import { ArrowLeftRight, Boxes, Building2, CheckCircle2, DollarSign, Eye, History, Package, Pencil, Plus, Receipt, RotateCcw, Search, ShoppingCart, Trash2, TrendingUp } from 'lucide-react';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n-context.js';

export type StoreSubTab = 'inventory' | 'sales_invoices' | 'sales_returns' | 'warehouse_transfers';

export interface StoreWarehousePill {
  readonly id: string;
  readonly name: string;
  readonly count: number;
}

export interface StoreProductCard {
  readonly key: string;
  readonly productId: string;
  readonly warehouseId: string | null;
  readonly name: string;
  readonly stockLabel: string;
  readonly inStock: boolean;
  readonly warehouseName: string;
  readonly costLabel: string;
  readonly saleLabel: string;
}

export interface StoreKpis {
  readonly items: number;
  readonly unitsLabel: string;
  readonly costLabel: string;
  readonly saleLabel: string;
  readonly profitLabel: string;
}

export interface StoreTransferRow {
  readonly id: string;
  readonly no: number;
  readonly dateLabel: string;
  readonly item: string;
  readonly note: string;
  readonly operator: string;
  readonly cancelled: boolean;
}

export interface StoreProps {
  readonly themeColor: string;
  readonly badgeCount: number;
  readonly subTab: StoreSubTab;
  readonly onSubTab: (t: StoreSubTab) => void;
  readonly counts: { readonly inventory: number; readonly sales: number; readonly returns: number; readonly transfers: number };
  readonly canManageWarehouses: boolean;
  readonly canOperate: boolean;
  readonly onManageWarehouses: () => void;
  readonly onAddProduct: () => void;
  readonly onSellHeader: () => void;
  readonly onNewSellList: () => void;
  // inventory
  readonly showAllPill: boolean;
  readonly allCount: number;
  readonly warehousePills: readonly StoreWarehousePill[];
  readonly selectedWarehouseId: string;
  readonly onSelectWarehouse: (id: string) => void;
  readonly kpis: StoreKpis;
  readonly cards: readonly StoreProductCard[];
  readonly onHistory: (card: StoreProductCard) => void;
  readonly onRemove: (card: StoreProductCard) => void;
  readonly onEdit: (card: StoreProductCard) => void;
  readonly onBuy: (card: StoreProductCard) => void;
  readonly onSell: (card: StoreProductCard) => void;
  // transfers archive
  readonly transferSearch: string;
  readonly onTransferSearch: (v: string) => void;
  readonly transfersTotal: number;
  readonly activeWarehouses: number;
  readonly transferRows: readonly StoreTransferRow[];
  readonly onTransferDetails: (id: string) => void;
  /** sales invoices / returns tabs (StoreSalesAndReturns) */
  readonly salesAndReturns: ReactNode;
}

const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

export function Store(p: StoreProps) {
  const { t } = useI18n();
  const tabBtn = (key: StoreSubTab, icon: ReactNode, label: string, count: number, tone: 'blue' | 'rose' | 'amber') => {
    const active = p.subTab === key;
    const activeText = tone === 'rose' ? 'text-rose-600 dark:text-rose-400' : tone === 'amber' ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400';
    const activeBadge = tone === 'rose' ? 'bg-rose-50 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400' : tone === 'amber' ? 'bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400' : 'bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400';
    return (
      <button
        type="button"
        data-testid={`store-tab-${key}`}
        onClick={() => p.onSubTab(key)}
        className={`w-full sm:flex-1 py-3 px-4 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-2.5 cursor-pointer ${
          active ? `bg-white dark:bg-[#21262d] ${activeText} shadow-md shadow-black/5` : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
        }`}
      >
        {icon}
        <span>{label}</span>
        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono font-black ${active ? activeBadge : 'bg-gray-200 dark:bg-[#30363d] text-gray-700 dark:text-gray-300'}`}>{count}</span>
      </button>
    );
  };

  return (
    <motion.div key="store" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6" data-testid="store-screen">
      {/* Top Navigation & Header Bar */}
      <div className="bg-white dark:bg-[#0d1117] p-6 rounded-3xl shadow-sm border border-gray-100 dark:border-[#30363d] dark:digital-border flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center shadow-lg shadow-blue-500/20 shrink-0">
            <Package size={28} />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-black text-gray-900 dark:text-[#f0f6fc] tracking-tight">{t('store')}</h2>
              <span data-testid="store-badge" className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-black px-3 py-1 rounded-full border border-blue-200 dark:border-blue-800/40">
                {p.badgeCount} {t('st.items_registered')}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-1">{t('st.subtitle')}</p>
          </div>
        </div>

        {/* Quick Header Actions */}
        <div className="flex items-center gap-3 flex-wrap w-full lg:w-auto">
          {p.canManageWarehouses && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="store-manage-warehouses"
              onClick={p.onManageWarehouses}
              className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 rounded-2xl text-xs font-black flex items-center gap-2 transition-all shadow-md shadow-indigo-600/20 cursor-pointer"
            >
              <Building2 size={16} />
              <span>{t('st.manage_warehouses')}</span>
            </motion.button>
          )}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="store-add-product"
            onClick={p.onAddProduct}
            className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2.5 rounded-2xl text-xs font-black flex items-center gap-2 transition-all shadow-md shadow-emerald-600/20 cursor-pointer"
          >
            <Plus size={16} />
            <span>{t('st.add_product')}</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="store-sell-product"
            onClick={p.onSellHeader}
            className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-2.5 rounded-2xl text-xs font-black flex items-center gap-2 transition-all shadow-md shadow-amber-600/20 cursor-pointer"
          >
            <ShoppingCart size={16} />
            <span>{t('st.sell_product')}</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="store-new-sell-list"
            onClick={p.onNewSellList}
            style={{ backgroundColor: p.themeColor }}
            className="bg-primary-600 hover:bg-primary-500 text-white px-4 py-2.5 rounded-2xl text-xs font-black flex items-center gap-2 transition-all shadow-md cursor-pointer"
          >
            <Receipt size={16} />
            <span>{t('st.new_sell_list')}</span>
          </motion.button>
        </div>
      </div>

      {/* Sub-Tabs Navigation Segmented Bar */}
      <div className="bg-gray-100/90 dark:bg-[#161b22] p-1.5 rounded-2xl border border-gray-200/80 dark:border-[#30363d] flex flex-col sm:flex-row items-center gap-1.5">
        {tabBtn('inventory', <Package size={17} />, t('st.tab_inventory'), p.counts.inventory, 'blue')}
        {tabBtn('sales_invoices', <Receipt size={17} />, t('st.tab_sales'), p.counts.sales, 'blue')}
        {tabBtn('sales_returns', <RotateCcw size={17} />, t('st.tab_returns'), p.counts.returns, 'rose')}
        {tabBtn('warehouse_transfers', <ArrowLeftRight size={17} />, t('st.tab_transfers'), p.counts.transfers, 'amber')}
      </div>

      {/* SUB-VIEW 1: INVENTORY & PRODUCTS */}
      {p.subTab === 'inventory' && (
        <div className="space-y-6">
          {/* Warehouses Selection Filter Bar */}
          <div className="bg-white dark:bg-[#0d1117] p-4 rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Building2 size={18} className="text-blue-600 dark:text-blue-400 shrink-0" />
              <span className="text-xs font-black text-gray-800 dark:text-gray-200">{t('st.filter_by_warehouse')}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap w-full md:w-auto">
              {p.showAllPill && (
                <button
                  type="button"
                  data-testid="store-wh-all"
                  onClick={() => p.onSelectWarehouse('all')}
                  className={`py-2 px-3.5 rounded-xl text-xs font-black transition-all flex items-center gap-2 cursor-pointer ${
                    p.selectedWarehouseId === 'all' ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' : 'bg-gray-100 dark:bg-[#161b22] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#21262d]'
                  }`}
                >
                  <span>{t('st.all_stores')}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-bold ${p.selectedWarehouseId === 'all' ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-[#30363d] text-gray-700 dark:text-gray-300'}`}>{p.allCount}</span>
                </button>
              )}
              {p.warehousePills.map((wh) => {
                const isSelected = p.selectedWarehouseId === wh.id;
                return (
                  <button
                    key={`wh-pill-${wh.id}`}
                    type="button"
                    data-testid={`store-wh-${wh.id}`}
                    onClick={() => p.onSelectWarehouse(wh.id)}
                    className={`py-2 px-3.5 rounded-xl text-xs font-black transition-all flex items-center gap-2 cursor-pointer ${
                      isSelected ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' : 'bg-gray-100 dark:bg-[#161b22] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#21262d]'
                    }`}
                  >
                    <span>🏢 {wh.name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-bold ${isSelected ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-[#30363d] text-gray-700 dark:text-gray-300'}`}>{wh.count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Inventory KPI Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm relative overflow-hidden">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('st.kpi_items')}</span>
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                  <Package size={20} />
                </div>
              </div>
              <div className="text-2xl font-black font-mono text-gray-900 dark:text-white" data-testid="store-kpi-items">
                {p.kpis.items} <span className="text-xs font-bold text-gray-400">{t('st.kpi_items_unit')}</span>
              </div>
              <p className="text-[11px] text-gray-500 font-medium mt-1">{t('st.kpi_items_sub')}</p>
            </div>
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm relative overflow-hidden">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('st.kpi_units')}</span>
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                  <Boxes size={20} />
                </div>
              </div>
              <div className="text-2xl font-black font-mono text-emerald-600 dark:text-emerald-400" data-testid="store-kpi-units">
                {p.kpis.unitsLabel} <span className="text-xs font-bold text-gray-400">{t('st.kpi_units_unit')}</span>
              </div>
              <p className="text-[11px] text-gray-500 font-medium mt-1">{t('st.kpi_units_sub')}</p>
            </div>
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm relative overflow-hidden">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('st.kpi_cost')}</span>
                <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                  <DollarSign size={20} />
                </div>
              </div>
              <div className="text-2xl font-black font-mono text-gray-900 dark:text-white" data-testid="store-kpi-cost">
                {p.kpis.costLabel} <span className="text-xs font-bold text-gray-400">د.ع</span>
              </div>
              <p className="text-[11px] text-gray-500 font-medium mt-1">{t('st.kpi_cost_sub')}</p>
            </div>
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm relative overflow-hidden">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('st.kpi_sale')}</span>
                <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                  <TrendingUp size={20} />
                </div>
              </div>
              <div className="text-2xl font-black font-mono text-purple-600 dark:text-purple-400" data-testid="store-kpi-sale">
                {p.kpis.saleLabel} <span className="text-xs font-bold text-gray-400">د.ع</span>
              </div>
              <p className="text-[11px] text-purple-600 dark:text-purple-400 font-bold mt-1">{t('st.kpi_potential', { amount: p.kpis.profitLabel })}</p>
            </div>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {p.cards.map((card) => (
              <motion.div key={card.key} variants={itemVariants} whileHover={{ y: -5 }} className="premium-card p-6 flex flex-col gap-4 group relative overflow-hidden" data-testid={`store-card-${card.key}`}>
                <div className="absolute top-0 right-0 w-24 h-24 bg-primary-500/5 rounded-full -mr-12 -mt-12 blur-2xl group-hover:bg-primary-500/10 transition-all duration-700" />
                <div className="flex justify-between items-start relative z-10">
                  <div className="w-14 h-14 rounded-2xl bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center text-primary-600 dark:text-primary-400 border border-primary-100/50 dark:border-primary-500/10 shadow-inner group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                    <Package size={28} />
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <button
                      onClick={() => p.onHistory(card)}
                      className="p-2.5 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-xl transition-colors border border-transparent hover:border-primary-100 dark:hover:border-primary-500/20"
                      title={t('st.history')}
                    >
                      <History size={18} />
                    </button>
                    {p.canOperate && (
                      <button
                        onClick={() => p.onEdit(card)}
                        data-testid={`store-edit-${card.key}`}
                        className="p-2.5 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-xl transition-colors border border-transparent hover:border-amber-100 dark:hover:border-amber-500/20"
                        title={t('st.edit_product')}
                      >
                        <Pencil size={18} />
                      </button>
                    )}
                    <button
                      onClick={() => p.onRemove(card)}
                      data-testid={`store-remove-${card.key}`}
                      className="p-2.5 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-xl transition-colors border border-transparent hover:border-rose-100 dark:hover:border-rose-500/20"
                      title={t('st.remove_title')}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                <div className="relative z-10">
                  <h4 className="font-black text-xl text-slate-800 dark:text-white uppercase tracking-tight leading-tight">{card.name}</h4>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span
                      className={`text-[10px] font-black px-3 py-1 rounded-full border uppercase tracking-widest ${card.inStock ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-500/20' : 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 border-rose-100 dark:border-rose-500/20'}`}
                    >
                      {card.stockLabel} {t('inventory')}
                    </span>
                    <span className="text-[10px] font-black px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200/60 dark:border-blue-800/40 flex items-center gap-1">
                      <Building2 size={11} />
                      <span>{card.warehouseName}</span>
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100 dark:border-slate-800 relative z-10">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">{t('costPrice')}</p>
                    <p className="font-black text-slate-600 dark:text-slate-400 font-mono text-lg">{card.costLabel}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">{t('salePrice')}</p>
                    <p className="font-black text-primary-600 dark:text-primary-400 font-mono text-lg digital-glow">{card.saleLabel}</p>
                  </div>
                </div>
                <div className="flex gap-3 mt-2 relative z-10">
                  <button
                    onClick={() => p.onBuy(card)}
                    data-testid={`store-buy-${card.key}`}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 active:scale-95 cursor-pointer"
                  >
                    <Plus size={16} />
                    {t('buy')}
                  </button>
                  <button
                    onClick={() => p.onSell(card)}
                    data-testid={`store-sell-${card.key}`}
                    className="flex-1 bg-primary-600 hover:bg-primary-500 text-white py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary-500/20 active:scale-95 cursor-pointer"
                  >
                    <ShoppingCart size={16} />
                    {t('sell')}
                  </button>
                </div>
              </motion.div>
            ))}
            <motion.button
              variants={itemVariants}
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
              onClick={p.onAddProduct}
              className="bg-slate-50 dark:bg-slate-900/30 border-2 border-dashed border-slate-200 dark:border-slate-800 p-8 rounded-[2.5rem] flex flex-col items-center justify-center gap-4 text-slate-400 dark:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-900/50 hover:border-primary-500/50 dark:hover:border-primary-500/50 transition-all group cursor-pointer"
            >
              <div className="w-16 h-16 rounded-full bg-white dark:bg-slate-800 flex items-center justify-center shadow-premium group-hover:scale-110 group-hover:rotate-90 transition-all duration-500 border border-slate-100 dark:border-slate-700 text-primary-500">
                <Plus size={32} />
              </div>
              <span className="font-black uppercase tracking-widest text-xs group-hover:text-primary-500 transition-colors">{t('add')}</span>
            </motion.button>
            <motion.button
              variants={itemVariants}
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
              onClick={p.onNewSellList}
              className="bg-slate-50 dark:bg-slate-900/30 border-2 border-dashed border-slate-200 dark:border-slate-800 p-8 rounded-[2.5rem] flex flex-col items-center justify-center gap-4 text-slate-400 dark:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-900/50 hover:border-primary-500/50 dark:hover:border-primary-500/50 transition-all group cursor-pointer"
            >
              <div className="w-16 h-16 rounded-full bg-white dark:bg-slate-800 flex items-center justify-center shadow-premium group-hover:scale-110 group-hover:rotate-180 transition-all duration-500 border border-slate-100 dark:border-slate-700 text-primary-500">
                <Receipt size={32} />
              </div>
              <span className="font-black uppercase tracking-widest text-xs group-hover:text-primary-500 transition-colors">{t('st.new_sell_list')}</span>
            </motion.button>
          </div>
        </div>
      )}

      {/* SUB-VIEWS 2 & 3: SALES INVOICES & SALES RETURNS */}
      {(p.subTab === 'sales_invoices' || p.subTab === 'sales_returns') && p.salesAndReturns}

      {/* SUB-VIEW 4: WAREHOUSE TRANSFERS ARCHIVE */}
      {p.subTab === 'warehouse_transfers' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-[#0d1117] p-5 rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-2xl">
                <ArrowLeftRight size={22} />
              </div>
              <div>
                <h3 className="text-base font-black text-gray-900 dark:text-white">{t('st.tab_transfers')}</h3>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-0.5">{t('st.transfers_sub')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 sm:w-64">
                <Search size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={p.transferSearch}
                  onChange={(e) => p.onTransferSearch(e.target.value)}
                  placeholder={t('st.transfers_search')}
                  className="w-full pr-10 pl-4 py-2.5 rounded-xl bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] text-xs font-bold text-gray-800 dark:text-gray-200 focus:outline-none focus:border-amber-500"
                />
              </div>
              {p.canManageWarehouses && (
                <button
                  type="button"
                  data-testid="store-new-transfer"
                  onClick={p.onManageWarehouses}
                  className="px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-black text-xs flex items-center gap-2 shadow-md shadow-amber-500/20 transition-all cursor-pointer"
                >
                  <Plus size={16} />
                  <span>{t('st.new_transfer')}</span>
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-[#0d1117] p-4 rounded-2xl border border-gray-100 dark:border-[#30363d] shadow-sm flex items-center gap-3.5">
              <div className="p-3 bg-amber-500/10 text-amber-500 rounded-xl">
                <ArrowLeftRight size={20} />
              </div>
              <div>
                <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400">{t('st.transfers_total')}</div>
                <div className="text-lg font-black text-gray-900 dark:text-white font-mono mt-0.5">{p.transfersTotal}</div>
              </div>
            </div>
            <div className="bg-white dark:bg-[#0d1117] p-4 rounded-2xl border border-gray-100 dark:border-[#30363d] shadow-sm flex items-center gap-3.5">
              <div className="p-3 bg-blue-500/10 text-blue-500 rounded-xl">
                <Package size={20} />
              </div>
              <div>
                <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400">{t('st.transfers_warehouses')}</div>
                <div className="text-lg font-black text-gray-900 dark:text-white font-mono mt-0.5">{p.activeWarehouses}</div>
              </div>
            </div>
            <div className="bg-white dark:bg-[#0d1117] p-4 rounded-2xl border border-gray-100 dark:border-[#30363d] shadow-sm flex items-center gap-3.5">
              <div className="p-3 bg-emerald-500/10 text-emerald-500 rounded-xl">
                <CheckCircle2 size={20} />
              </div>
              <div>
                <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400">{t('st.transfers_matching')}</div>
                <div className="text-lg font-black text-gray-900 dark:text-white font-mono mt-0.5">{p.transferRows.length}</div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-[#0d1117] rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm overflow-hidden">
            {p.transferRows.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <ArrowLeftRight size={32} />
                </div>
                <h4 className="text-sm font-black text-gray-800 dark:text-gray-200">{t('st.transfers_empty')}</h4>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">{t('st.transfers_empty_sub')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-right border-collapse">
                  <thead>
                    <tr className="bg-gray-50/80 dark:bg-[#161b22] border-b border-gray-100 dark:border-[#30363d] text-[11px] font-black text-gray-500 dark:text-gray-400">
                      <th className="py-3.5 px-4">#</th>
                      <th className="py-3.5 px-4">{t('st.col_datetime')}</th>
                      <th className="py-3.5 px-4">{t('st.col_transfer_details')}</th>
                      <th className="py-3.5 px-4">{t('st.col_note')}</th>
                      <th className="py-3.5 px-4">{t('st.col_operator_exec')}</th>
                      <th className="py-3.5 px-4 text-center">{t('st.col_action')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-[#30363d] text-xs font-semibold">
                    {p.transferRows.map((m) => (
                      <tr key={m.id} className={`hover:bg-gray-50/50 dark:hover:bg-[#161b22]/50 transition-colors ${m.cancelled ? 'opacity-50 line-through' : ''}`} data-testid={`store-transfer-${m.id}`}>
                        <td className="py-3.5 px-4 font-mono font-bold text-gray-400">{m.no}</td>
                        <td className="py-3.5 px-4 text-gray-600 dark:text-gray-400 whitespace-nowrap font-mono text-[11px]">{m.dateLabel}</td>
                        <td className="py-3.5 px-4">
                          <div className="font-black text-gray-900 dark:text-white flex items-center gap-2">
                            <span className="p-1 rounded bg-amber-500/10 text-amber-600 shrink-0">
                              <ArrowLeftRight size={14} />
                            </span>
                            <span>{m.item}</span>
                          </div>
                        </td>
                        <td className="py-3.5 px-4 text-gray-600 dark:text-gray-300">{m.note || '-'}</td>
                        <td className="py-3.5 px-4">
                          <span className="px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-[#21262d] text-gray-700 dark:text-gray-300 font-bold text-[11px]">{m.operator}</span>
                        </td>
                        <td className="py-3.5 px-4 text-center whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => p.onTransferDetails(m.id)}
                            className="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors cursor-pointer"
                            title={t('st.view_details')}
                          >
                            <Eye size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}
